import { useEffect, useState } from "react";
import { Sparkles, Loader2, RefreshCw, Columns3 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { AiPromptLibrary } from "./AiPromptLibrary";

const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const md = (s) => esc(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
const txt = (el) => (el.textContent || "").replace(/\s+/g, " ").trim();
const RE_NRIF = /nostri\s+rif/i;
const RE_VRIF = /vostri\s+rif/i;
const RE_OGG = /^oggetto\s*:/i;
const RE_CLOSE = /(distinti|cordiali)\s+saluti|con\s+osservanza|ossequi/i;
const isClose = (el) => RE_CLOSE.test(txt(el)) || !!el.querySelector?.('img[alt="Firma"]');

// Stili del testo del template (paragrafo + span) per i paragrafi generati.
const styleOf = (root) => {
  const p = [...root.querySelectorAll("p")].find((x) => RE_OGG.test(txt(x))) || [...root.querySelectorAll("p")].find((x) => txt(x));
  const span = p?.querySelector("span[style]") || root.querySelector("span[style]");
  return { p: p?.getAttribute("style") || "text-align: justify; line-height: 1.15;", s: span?.getAttribute("style") || "" };
};
const mkP = (st, inner) => `<p style="${st.p}">${st.s ? `<span style="${st.s}">${inner}</span>` : inner}</p>`;
const rifInner = (r) => `<strong>Nostri riferimenti:</strong> ${md(r.nostri_riferimenti)}${r.vostri_riferimenti ? `<br><strong>Vostri riferimenti:</strong> ${md(r.vostri_riferimenti)}` : ""}`;
const oggInner = (r) => `<strong>Oggetto:</strong> ${md(r.oggetto)}`;
const setInner = (el, st, inner) => { el.innerHTML = st.s ? `<span style="${st.s}">${inner}</span>` : inner; };

// Se la chiusura è nello stesso paragrafo del testo, elimina il testo che la precede.
const trimBeforeClose = (el) => {
  const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let n;
  while ((n = w.nextNode())) {
    const m = n.nodeValue.match(RE_CLOSE);
    if (!m) continue;
    const rg = document.createRange();
    rg.setStart(el, 0);
    rg.setEnd(n, m.index);
    rg.deleteContents();
    el.querySelectorAll("br").forEach((br) => { if (!txt(el).length || br.previousSibling == null) br.remove(); });
    return;
  }
};

const applyAi = (ed, r) => {
  const root = document.createElement("div");
  root.innerHTML = ed.getHTML();
  const st = styleOf(root);
  const kids = [...root.children];
  const iOgg = kids.findIndex((el) => RE_OGG.test(txt(el)));
  const iClose = iOgg >= 0 ? kids.findIndex((el, i) => i > iOgg && isClose(el)) : -1;
  const hasN = kids.some((el) => RE_NRIF.test(txt(el)));
  const hasV = kids.some((el) => RE_VRIF.test(txt(el)));
  const gap = mkP(st, "&nbsp;");
  const stJ = { ...st, p: /text-align\s*:/i.test(st.p) ? st.p.replace(/text-align\s*:\s*[a-z]+/i, "text-align: justify") : `text-align: justify; ${st.p}` };
  const corpo = gap + (r.corpo || []).map((p) => mkP(stJ, md(p))).join(gap) + gap;
  const extra = (hasN ? "" : mkP(st, rifInner({ ...r, vostri_riferimenti: hasV ? "" : r.vostri_riferimenti }))) + (iOgg >= 0 ? "" : mkP(st, oggInner(r)));
  kids.forEach((el) => {
    if (RE_NRIF.test(txt(el))) setInner(el, st, rifInner({ ...r, vostri_riferimenti: hasV ? "" : r.vostri_riferimenti }));
    else if (RE_VRIF.test(txt(el)) && r.vostri_riferimenti) setInner(el, st, `<strong>Vostri riferimenti:</strong> ${md(r.vostri_riferimenti)}`);
    else if (RE_OGG.test(txt(el)) && r.oggetto) setInner(el, st, oggInner(r));
  });
  if (iOgg >= 0 && iClose > iOgg) {
    kids.slice(iOgg + 1, iClose).forEach((el) => el.remove());
    trimBeforeClose(kids[iClose]);
    kids[iOgg].insertAdjacentHTML("afterend", corpo);
    if (extra) kids[iOgg].insertAdjacentHTML("beforebegin", extra);
    ed.commands.setContent(root.innerHTML, true);
    return;
  }
  const { from } = ed.state.selection;
  ed.commands.setContent(root.innerHTML, true);
  const pos = Math.min(from, ed.state.doc.content.size);
  ed.chain().focus().setTextSelection(pos).insertContent(extra + corpo).run();
};

const CompareVersions = ({ series, onPick, onClose }) => (
  <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
    <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-[95vw] w-[95vw] max-h-[88vh] flex flex-col" data-testid="ai-compare-dialog">
      <DialogHeader><DialogTitle className="flex items-center gap-2"><Columns3 className="h-4 w-4 text-teal-400" />Confronta versioni</DialogTitle></DialogHeader>
      <p className="text-xs text-slate-400 -mt-2">Richiesta: «{series.prompt}»</p>
      <div className="grid gap-3 overflow-auto flex-1 min-h-0" style={{ gridTemplateColumns: `repeat(${series.versions.length}, minmax(260px, 1fr))` }}>
        {series.versions.map((v, i) => (
          <div key={i} data-testid={`ai-compare-col-${i + 1}`} className={`flex flex-col rounded-lg border ${i === series.cur ? "border-teal-500" : "border-slate-700"} bg-slate-900/60 min-h-0`}>
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700">
              <span className="font-mono text-sm text-teal-600">V{i + 1}{i === series.cur ? " · in uso" : ""}</span>
              <button type="button" onClick={() => onPick(i)} data-testid={`ai-compare-use-${i + 1}`}
                className="h-8 px-3 text-xs font-semibold rounded-md bg-teal-600 text-white hover:bg-teal-500">Usa questa</button>
            </div>
            <div className="p-3 text-sm leading-relaxed overflow-auto space-y-2">
              {v.oggetto && <div><span className="font-semibold text-slate-300">Oggetto: </span><span className="text-slate-200" dangerouslySetInnerHTML={{ __html: md(v.oggetto) }} /></div>}
              {(v.nostri_riferimenti || v.vostri_riferimenti) && (
                <div className="text-xs text-slate-400">{v.nostri_riferimenti && <div>Nostri rif.: {v.nostri_riferimenti}</div>}{v.vostri_riferimenti && <div>Vostri rif.: {v.vostri_riferimenti}</div>}</div>
              )}
              {(Array.isArray(v.corpo) ? v.corpo : [v.corpo]).filter(Boolean).map((p, j) => (
                <p key={j} className="text-slate-200" dangerouslySetInnerHTML={{ __html: md(p) }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);

export const AiLetterButton = ({ editorRef, praticaId, tipoLettera, templateId }) => {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [series, setSeries] = useState(null); // { prompt, base, pos, versions: [data], cur }
  const [compare, setCompare] = useState(false);

  useEffect(() => {
    api.get("/studio/lettera-ai/enabled").then(({ data }) => setEnabled(!!data?.enabled)).catch(() => setEnabled(false));
  }, []);
  useEffect(() => { setSeries(null); }, [templateId, tipoLettera]);

  const restore = (ed, s) => {
    ed.commands.setContent(s.base, true);
    ed.commands.setTextSelection(Math.min(s.pos, ed.state.doc.content.size));
  };
  const generate = async (text, s) => {
    const ed = editorRef.current;
    if (!ed) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/studio/pratiche/${praticaId}/lettera-ai`, { prompt: text, tipo_lettera: tipoLettera || "", template_id: templateId || null });
      const cur = s || { prompt: text, base: ed.getHTML(), pos: ed.state.selection.from, versions: [] };
      if (s) restore(ed, cur);
      applyAi(ed, data);
      const versions = [...cur.versions, data];
      setSeries({ ...cur, versions, cur: versions.length - 1 });
      setPrompt("");
      toast.success(s ? `Versione ${versions.length} generata: puoi tornare alle precedenti` : "Testo generato: puoi modificarlo prima di salvare");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore AI");
    } finally { setBusy(false); }
  };
  const run = () => {
    if (!prompt.trim()) { toast.error("Scrivi un'indicazione per l'AI"); return; }
    generate(prompt.trim(), null);
  };
  const pick = (i) => {
    const ed = editorRef.current;
    if (!ed || !series || i === series.cur) return;
    restore(ed, series);
    applyAi(ed, series.versions[i]);
    setSeries({ ...series, cur: i });
  };

  if (!praticaId || !enabled) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="ai-letter-open-btn" className="h-9 px-3 text-xs font-semibold rounded-md bg-teal-600 text-white hover:bg-teal-500 transition-colors inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" />Scrivi con AI</button>
      {series && (
        <span className="inline-flex items-center gap-1" data-testid="ai-letter-versions">
          <button type="button" onClick={() => generate(series.prompt, series)} disabled={busy || series.versions.length >= 5} data-testid="ai-letter-regenerate-btn"
            title={series.versions.length >= 5 ? "Massimo 5 versioni" : `Riprova la stessa richiesta: «${series.prompt.slice(0, 80)}»`}
            className="h-9 px-3 text-xs font-semibold rounded-md border border-teal-500/60 text-teal-600 hover:bg-teal-500/10 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}Rigenera
          </button>
          {series.versions.length > 1 && series.versions.map((_, i) => (
            <button key={i} type="button" onClick={() => pick(i)} disabled={busy} data-testid={`ai-letter-version-${i + 1}`} title={`Usa la versione ${i + 1}`}
              className={`h-9 min-w-9 px-2 text-xs font-mono rounded-md border transition-colors ${i === series.cur ? "bg-teal-600 border-teal-600 text-white" : "border-slate-400/50 text-slate-500 hover:bg-teal-500/10"}`}>V{i + 1}</button>
          ))}
          {series.versions.length > 1 && (
            <button type="button" onClick={() => setCompare(true)} disabled={busy} data-testid="ai-letter-compare-btn" title="Confronta le versioni una accanto all'altra"
              className="h-9 px-3 text-xs font-semibold rounded-md border border-slate-400/50 text-slate-500 hover:bg-teal-500/10 inline-flex items-center gap-1.5"><Columns3 className="h-3.5 w-3.5" />Confronta</button>
          )}
        </span>
      )}
      {series && compare && <CompareVersions series={series} onPick={(i) => { pick(i); setCompare(false); }} onClose={() => setCompare(false)} />}
      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-lg" data-testid="ai-letter-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-teal-400" />Scrivi con AI</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400">L'AI legge i dati della pratica e compila Oggetto, Riferimenti e corpo della lettera, mantenendo il layout del modello.</p>
          <AiPromptLibrary prompt={prompt} setPrompt={setPrompt} disabled={busy} />
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} autoFocus disabled={busy}
            placeholder="Es. scrivi una lettera di messa in mora alla compagnia" data-testid="ai-letter-prompt"
            className="w-full rounded-md bg-slate-900 border border-slate-600 p-3 text-sm text-slate-100 caret-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-500" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} disabled={busy} data-testid="ai-letter-cancel-btn" className="h-9 px-4 text-sm rounded-md border border-slate-600 hover:bg-slate-800">Annulla</button>
            <button type="button" onClick={run} disabled={busy} data-testid="ai-letter-generate-btn" className="h-9 px-4 text-sm font-semibold rounded-md bg-teal-600 text-white hover:bg-teal-500 inline-flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}{busy ? "Generazione..." : "Genera"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
